import React ,{useState}from "react";
import { Container, Row, Col, Card, ListGroup,Button } from "react-bootstrap";
import Navbar from "../Navbar/Navbar";
import { FaThumbsUp, FaThumbsDown ,FaBars} from "react-icons/fa";


/**
 * Clean, professional Privacy Policy page using React-Bootstrap.
 *
 * Props (all optional):
 * - companyName: string
 * - supportEmail: string
 * - addressLine: string
 * - phone: string
 * - lastUpdated: string (e.g., "August 20, 2025")
 */
export default function PrivacyPolicy({
  companyName = "House Clean",
  supportEmail = "support@houseclean.com",
  addressLine = "123 Example Street, New Delhi, IN",
  phone = "+91 90000 00000",
  lastUpdated = "August 20, 2025",
}) {
  const sections = [
    { id: "introduction", title: "Introduction" },
    { id: "info-we-collect", title: "Information We Collect" },
    { id: "how-we-use", title: "How We Use Your Information" },
    { id: "legal-bases", title: "Legal Bases (GDPR)" },
    { id: "cookies", title: "Cookies & Tracking" },
    { id: "sharing", title: "When We Share Your Data" },
    { id: "international", title: "International Transfers" },
    { id: "retention", title: "Data Retention" },
    { id: "your-rights", title: "Your Rights" },
    { id: "security", title: "Security" },
    { id: "children", title: "Children’s Privacy" },
    { id: "changes", title: "Changes to This Policy" },
    { id: "contact", title: "Contact Us" },
  ];
    const [feedback, setFeedback] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [tocOpen, setTocOpen] = useState(false);

  const handleFeedback = (type) => {
    setFeedback(type);
    setSubmitted(true);
    // TODO: send to backend API if needed
  };

  return (
    <>
    <Navbar/>
    <Container  className="bg-light p-15 " >
           <br/>
                <header className="mb-4">
                  <h1 className="h3 mb-1">Privacy Policy</h1>
                  <p className="text-muted mb-0">
                    Last updated: {lastUpdated} • {companyName}
                  </p>
                </header>

                {/* Introduction */}
                <section id="introduction" className="mb-4" >
                  <h2 className="h5">Introduction</h2>
                  <p className="mb-0">
                    This Privacy Policy explains how {companyName} ("we", "our",
                    or "us") collects, uses, and safeguards your information when
                    you access our website, mobile application, products, and
                    services (collectively, the "Services"). By using the
                    Services, you agree to the practices described in this Policy.
                  </p>
                </section>

                {/* Information We Collect */}
                <section id="info-we-collect" className="mb-4">
                  <h2 className="h5">Information We Collect</h2>
                  <ul>
                    <li>
                      <strong>Account data:</strong> name, email, phone number,
                      address, profile details.
                    </li>
                    <li>
                      <strong>Order & billing data:</strong> items purchased,
                      transaction IDs, payment method (tokenized), invoices.
                    </li>
                    <li>
                      <strong>Usage data:</strong> pages viewed, actions taken,
                      referring URLs, device identifiers, approximate location,
                      IP address.
                    </li>
                    <li>
                      <strong>Support data:</strong> messages, attachments, and
                      feedback you send to us.
                    </li>
                  </ul>
                  <p className="mb-0 small text-muted">
                    We collect data directly from you, automatically when you use
                    the Services, and in some cases from third parties (e.g.,
                    analytics or payment providers).
                  </p>
                </section>

                {/* How We Use */}
                <section id="how-we-use" className="mb-4">
                  <h2 className="h5">How We Use Your Information</h2>
                  <ul>
                    <li>Provide, maintain, and improve the Services.</li>
                    <li>Process orders, payments, and fulfill requests.</li>
                    <li>Send transactional messages (receipts, alerts, updates).</li>
                    <li>Personalize content and recommendations.</li>
                    <li>Monitor usage, prevent fraud, and ensure platform safety.</li>
                    <li>Comply with legal obligations and enforce terms.</li>
                    <li>Communicate about features, offers, and promotions (with your consent where required).</li>
                  </ul>
                </section>

                {/* Legal Bases */}
                <section id="legal-bases" className="mb-4">
                  <h2 className="h5">Legal Bases (GDPR)</h2>
                  <p>
                    Where applicable (e.g., in the EEA/UK), we process personal
                    data under these legal bases:
                  </p>
                  <ul>
                    <li><strong>Contract:</strong> to provide the Services you request.</li>
                    <li><strong>Legitimate interests:</strong> to operate, secure, and improve our Services.</li>
                    <li><strong>Consent:</strong> for optional features like marketing emails or certain cookies.</li>
                    <li><strong>Legal obligation:</strong> to meet regulatory requirements and respond to lawful requests.</li>
                  </ul>
                </section>

                {/* Cookies */}
                <section id="cookies" className="mb-4">
                  <h2 className="h5">Cookies & Tracking</h2>
                  <p>
                    We use cookies, SDKs, and similar technologies to remember your
                    settings, keep you signed in, analyze traffic, and measure the
                    effectiveness of campaigns. You can control cookies through
                    your browser or device settings. Some features may not
                    function properly if certain cookies are disabled.
                  </p>
                </section>

                {/* Sharing */}
                <section id="sharing" className="mb-4">
                  <h2 className="h5">When We Share Your Data</h2>
                  <p>We do not sell your personal data. We may share information with:</p>
                  <ul>
                    <li>
                      <strong>Service providers:</strong> payment processors,
                      analytics providers, cloud hosting, customer support tools.
                    </li>
                    <li>
                      <strong>Business transfers:</strong> in connection with a
                      merger, acquisition, or asset sale.
                    </li>
                    <li>
                      <strong>Legal & safety:</strong> to comply with laws,
                      protect rights, and prevent harm or fraud.
                    </li>
                    <li>
                      <strong>With your consent:</strong> when you direct us to
                      share data or when required for optional features.
                    </li>
                  </ul>
                </section>

                {/* International Transfers */}
                <section id="international" className="mb-4">
                  <h2 className="h5">International Transfers</h2>
                  <p>
                    Your data may be processed in countries other than your own.
                    Where required, we implement appropriate safeguards (e.g.,
                    standard contractual clauses) to protect your information.
                  </p>
                </section>

                {/* Retention */}
                <section id="retention" className="mb-4">
                  <h2 className="h5">Data Retention</h2>
                  <p>
                    We keep personal data only as long as necessary for the
                    purposes described above, to comply with legal obligations,
                    resolve disputes, and enforce agreements. Retention periods
                    vary by data type and context.
                  </p>
                </section>

                {/* Your Rights */}
                <section id="your-rights" className="mb-4">
                  <h2 className="h5">Your Rights</h2>
                  <p>
                    Depending on your location, you may have rights to access,
                    correct, delete, restrict, or object to processing of your
                    personal data, and to portability. You can also withdraw
                    consent where processing relies on consent.
                  </p>
                  <p className="mb-0">
                    To exercise rights, contact us at {" "}
                    <a href={`mailto:${supportEmail}`}>{supportEmail}</a>. We may
                    request information to verify your identity.
                  </p>
                </section>

                {/* Security */}
                <section id="security" className="mb-4">
                  <h2 className="h5">Security</h2>
                  <p>
                    We use administrative, technical, and physical safeguards
                    designed to protect personal data. However, no method of
                    transmission or storage is completely secure.
                  </p>
                </section>

                {/* Children */}
                <section id="children" className="mb-4">
                  <h2 className="h5">Children’s Privacy</h2>
                  <p>
                    Our Services are not directed to children under the age of 13
                    (or the relevant age in your jurisdiction). We do not
                    knowingly collect personal data from children. If you believe a
                    child has provided us data, please contact us to remove it.
                  </p>
                </section>

                {/* Changes */}
                <section id="changes" className="mb-4">
                  <h2 className="h5">Changes to This Policy</h2>
                  <p>
                    We may update this Privacy Policy from time to time. We will
                    post the new version with an updated date and, where
                    appropriate, provide additional notice.
                  </p>
                </section>

                {/* Contact */}
                <section id="contact">
                  <h2 className="h5">Contact Us</h2>
                  <address className="mb-0">
                    <div className="mb-1">{companyName}</div>
                    <div className="mb-1">{addressLine}</div>
                    <div className="mb-1">Phone: {phone}</div>
                    <div>
                      Email: <a href={`mailto:${supportEmail}`}>{supportEmail}</a>
                    </div>
                  </address>
                    <div className="mt-4 p-3 border-top">
                    {!submitted ? (
                      <>
                        <p className="mb-2 fw-bold" style={{textAlign:"center"}}>Was this information helpful?</p>
                        <div className="d-flex gap-3 flex-wrap justify-content-center">
                          <Button
                            variant="outline-success"
                            className="d-flex align-items-center gap-2"
                            onClick={() => handleFeedback("like")}
                          >
                            <FaThumbsUp /> Yes
                          </Button>
                          <Button
                            variant="outline-danger"
                            className="d-flex align-items-center gap-2"
                            onClick={() => handleFeedback("dislike")}
                          >
                            <FaThumbsDown /> No
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-success mt-2" style={{textAlign:"center"}}>Thank you for your feedback! 🙏</p>
                    )}
                  </div>
                </section>
            

      
    </Container>
    </>
  );
}
